import type * as type from "@vTypes";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type SetStateAction,
} from "react";
import RosterListButton from "../../../components/feature/roster_view/RosterListButton";
import TableBodyView from "../../../components/feature/roster_view/TableBodyView";
import { RosterShiftLegendChips } from "@components/feature/rosterTable/RosterShiftLegendChips";
import { useRosterDisplayShiftColumns } from "@/hooks/shift/useRosterDisplayShiftColumns";
import { scheduleDownload_excel } from "@pages/roster-view/scheduleDownload.ts";
import type { Roster, ShiftManage } from "@vTypes/snapshot-type.ts";
import RosterMadeTable from "../../../components/feature/rosterTable/RosterMadeTable";
import type { ShiftLookupMaps } from "@/utils/shift-util";
import { useIsAdmin, useUser } from "@/hooks/useAuth";
import { useIsHeadNurse } from "@/hooks/useHeadNurse";
import { manPowerMonthlyFromShiftManage } from "@/utils/issuedRosterManpower";
import { getPrecepteePolicy } from "@/utils/precepteePolicy";
import { cn } from "@/lib/utils";
import TableHeader from "@components/feature/rosterTable/TableHeader.tsx";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import LoadingSpiner from "@/components/ui/LoadingSpiner";
import useRosterConfig from "@/hooks/useRosterConfig";
import type { NurseResponse } from "@/types/nurse-type";
import { Download } from "lucide-react";
import { BtnIcon } from "@/components/ui/button/btn-icon";
import IssuedDate from "./issued-date";
import type { UserData } from "@/types/user-type";
import type { IssuedAssignmentsViewBundle } from "@vTypes/snapshot-type";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  mergeWardOptionsWithUserHome,
  rawGroupIdFromSelectValue,
} from "@/pages/roster-view/utils/wardOptionsFromIssuedAssignments";

function RosterViewAllTabContent({
  shiftManager,
  issuedList,
  currentScheduleDate,
  setCurrentScheduleDate,
  holidayDates,
  selectedGroupId: _selectedGroupId,
  availableShifts,
  shiftLookupMaps,
  rosterSheet: _rosterSheet,
  monthlySchedule,
  issuedMemo = "",
  isRosterSnapshotLoading = false,
  /** `Roster_view` outlet — 스냅샷 정렬 시 `snapShotData.created_at` */
  issuedDate,
  loginUser = null,
  issuedAssignmentsView = null,
  issuedSnapshotGroupId = "",
  setIssuedSnapshotGroupId,
}: {
  shiftManager: ShiftManage | ShiftManage[];
  issuedList: { year: number; month: number; schedule_id: string }[];
  currentScheduleDate: { year: number; month: number; schedule_id: string };
  setCurrentScheduleDate: (value: {
    year: number;
    month: number;
    schedule_id: string;
  }) => void;
  holidayDates: string[];
  selectedGroupId: string | null;
  availableShifts: type.availableShifts[];
  shiftLookupMaps: ShiftLookupMaps<type.availableShifts>;
  rosterSheet: Roster | null;
  monthlySchedule: unknown[] | null;
  issuedMemo?: string;
  isRosterSnapshotLoading?: boolean;
  issuedDate?: Date;
  loginUser?: UserData | null;
  issuedAssignmentsView?: IssuedAssignmentsViewBundle | null;
  issuedSnapshotGroupId?: string;
  setIssuedSnapshotGroupId: (rawGroupId: string) => void;
}) {
  void _selectedGroupId;
  void _rosterSheet;
  const isAdmin = useIsAdmin();
  const isHeadNurse = useIsHeadNurse();
  const userData = useUser();
  const [showPerShiftStats, setShowPerShiftStats] = useState(false);

  const { data: rosterConfig } = useRosterConfig();
  /** `use_mid === false`면 미드(M) 시프트 행 제외 — 통계·열·범례 동일 기준 */
  const shiftsForView = useMemo(() => {
    if (rosterConfig?.use_mid !== false) return availableShifts;
    return (availableShifts ?? []).filter((s) => s.default_shift !== "M");
  }, [availableShifts, rosterConfig?.use_mid]);

  const viewPrecepteePolicy = useMemo(
    () => getPrecepteePolicy(rosterConfig ?? null),
    [rosterConfig]
  );

  /** 상위 스냅샷 데이터 — 별도 미러링 없이 그대로 사용 */
  const baseNurses = useMemo(() => monthlySchedule ?? [], [monthlySchedule]);

  /** TableHeader 열 정렬(이름·경력 등) 시에만 덮어씀. 월/스케줄 바뀌면 상위 데이터로 리셋 */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre_monthlyNurseSchedule 반환 타입
  const [sortedNurses, setSortedNurses] = useState<any[] | null>(null);

  useEffect(() => {
    setSortedNurses(null);
  }, [monthlySchedule, currentScheduleDate.schedule_id]);

  const displayNurses = sortedNurses ?? baseNurses;

  const isUseMid = rosterConfig?.use_mid;
  const { displayShifts: rosterViewShiftLegend } = useRosterDisplayShiftColumns(
    displayNurses as {
      schedule?: (string | null | { code: string } | null)[] | null;
      schedule_ids?: (number | null)[] | null;
    }[],
    shiftsForView,
    isUseMid,
    { includeBaselineMainTemplateColumns: true }
  );

  const setCopyNursesForHeader = useCallback(
    (value: SetStateAction<NurseResponse[]>) => {
      setSortedNurses((prev) => {
        const base = baseNurses;
        return typeof value === "function" ? value(prev ?? base) : value;
      });
    },
    [baseNurses]
  );

  const manPower_monthly = useMemo(
    () => manPowerMonthlyFromShiftManage(shiftManager),
    [shiftManager]
  );

  // RosterMadeTable 통계용: 프리셉티 제외
  const statsNurses = useMemo(
    () =>
      viewPrecepteePolicy.excludePrecepteeFromCount
        ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
          displayNurses.filter((n: any) => n.preceptor?.role !== "preceptee")
        : displayNurses,
    [displayNurses, viewPrecepteePolicy.excludePrecepteeFromCount]
  );

  const viewerNurseId =
    issuedAssignmentsView?.viewer?.nurse_id ?? loginUser?.nurse_id ?? "";

  /** API(groups) + 세션 소속 병동명·id 병합 — 동일 group_id는 유저 표시명 우선 */
  const wardSelectOptions = useMemo(() => {
    const groups = Array.isArray(issuedAssignmentsView?.groups)
      ? issuedAssignmentsView.groups
      : [];
    const groupOptions = groups
      .map((g) => {
        const rawGroupId = (g.group_id ?? "").trim();
        const label = (g.group_name ?? "").trim();
        if (rawGroupId === "" || label === "") return null;
        return {
          rawGroupId,
          value: rawGroupId,
          label,
        };
      })
      .filter(
        (
          v
        ): v is {
          rawGroupId: string;
          value: string;
          label: string;
        } => v != null
      );
    return mergeWardOptionsWithUserHome(groupOptions, userData);
  }, [issuedAssignmentsView?.groups, userData]);

  const wardSelectValueForUI = useMemo(() => {
    if (wardSelectOptions.length === 0) return "";
    const g = issuedSnapshotGroupId.trim();
    const hit = wardSelectOptions.find((o) => o.rawGroupId === g);
    return hit?.value ?? wardSelectOptions[0]!.value;
  }, [issuedSnapshotGroupId, wardSelectOptions]);

  console.log("wardSelectOptions:", wardSelectOptions);

  return (
    <div className="font-pretendard w-full">
      <div className="flex flex-col items-center gap-10">
        <div className="relative flex flex-col items-center w-full">
          <div className="w-full px-5  justify-center bg-white z-20 flex items-end sticky top-0 pt-2">
            <div>
              <div className="flex flex-col ">
                <div className="mb-4 flex w-full flex-wrap items-center  relative justify-between  gap-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <RosterListButton
                      issuedList={issuedList}
                      currentScheduleDate={currentScheduleDate}
                      setCurrentScheduleDate={setCurrentScheduleDate}
                    />

                    {wardSelectOptions.length > 1 && (
                      <Select
                        value={wardSelectValueForUI}
                        onValueChange={(v) =>
                          setIssuedSnapshotGroupId(rawGroupIdFromSelectValue(v))
                        }
                      >
                        <SelectTrigger
                          size="sm"
                          className="w-[180px] py-5!  font-pretendard"
                        >
                          <SelectValue placeholder="병동 선택" />
                        </SelectTrigger>
                        <SelectContent>
                          {wardSelectOptions.map((o) => (
                            <SelectItem
                              className="font-pretendard! text-sm!"
                              key={o.value}
                              value={o.value}
                            >
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    <IssuedDate issuedAtDisplay={issuedDate} />
                  </div>

                  {!isRosterSnapshotLoading && displayNurses.length > 0 && (
                    <BtnIcon
                      size={"icon-lg"}
                      disabled={
                        isRosterSnapshotLoading || monthlySchedule == null
                      }
                      onClick={async () => {
                        await scheduleDownload_excel(currentScheduleDate);
                      }}
                      tooltip="근무표 다운로드"
                      icon={<Download />}
                    />
                  )}
                </div>

                {!isRosterSnapshotLoading && displayNurses.length > 0 && (
                  <div
                    className={cn(
                      "relative mb-4 animate-in fade-in-0 duration-300 transition-opacity"
                    )}
                  >
                    <RosterMadeTable
                      gridType="ROSTER_ONLY_VIEW"
                      defaultShiftCode={shiftsForView}
                      year={currentScheduleDate.year}
                      month={currentScheduleDate.month}
                      showManpowerButton={false}
                      manPower_monthly={manPower_monthly}
                      memo={issuedMemo}
                      setMemo={() => {}}
                      isEditableMemo={false}
                      copyNurses={statsNurses}
                      shiftLookupMaps={shiftLookupMaps}
                      showPrecepteeBadge={
                        viewPrecepteePolicy.excludePrecepteeFromCount
                      }
                      role={
                        isAdmin ? "admin" : isHeadNurse ? "head_nurse" : "nurse"
                      }
                    />
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between gap-[10px] ">
                {!isRosterSnapshotLoading && displayNurses.length > 0 && (
                  <>
                    <TableHeader
                      defaultShiftCode={shiftsForView}
                      copyNurses={displayNurses}
                      setCopyNurses={setCopyNursesForHeader}
                      currentYear={currentScheduleDate.year}
                      currentMonth={currentScheduleDate.month}
                      holidayDates={holidayDates}
                    />
                    <div className="relative flex size-10 shrink-0 items-end">
                      <div className="absolute -bottom-[17px] -left-[1px] flex rounded-full bg-white">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className={cn(
                                "group flex size-7 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0",
                                "transition-opacity hover:opacity-90",
                                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-1"
                              )}
                              aria-pressed={showPerShiftStats}
                              aria-label={
                                showPerShiftStats
                                  ? "개별 근무코드 통계 숨기기"
                                  : "개별 근무코드 통계 보기"
                              }
                              onClick={() =>
                                setShowPerShiftStats((prev) => !prev)
                              }
                            >
                              <img
                                src="/asset/icon/config/icon_1.svg"
                                alt=""
                                aria-hidden
                                className={cn(
                                  "size-7 object-contain transition-[filter,transform] duration-200 ease-out brightness-100 group-hover:brightness-[0.55]",
                                  showPerShiftStats ? "rotate-90" : "-rotate-90"
                                )}
                                draggable={false}
                              />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>
                              {showPerShiftStats
                                ? "개별 근무코드 통계 숨기기"
                                : "개별 근무코드 통계 보기"}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            {showPerShiftStats && (
              <div className="mb-2.5 animate-in fade-in duration-200 -translate-x-[0px] pr-2">
                <RosterShiftLegendChips
                  displayShifts={rosterViewShiftLegend}
                  defaultShiftCode={shiftsForView}
                />
              </div>
            )}
          </div>

          <div className="relative  px-5 pb-30 ">
            <div
              className={cn(
                "animate-in fade-in-0 duration-300 transition-opacity relative"
              )}
            >
              <div className="relative">
                {isRosterSnapshotLoading ? (
                  <div className="flex items-center justify-center pt-10">
                    <LoadingSpiner msg="근무표를 불러오는 중입니다" />
                  </div>
                ) : (
                  <TableBodyView
                    shiftInfo={shiftsForView}
                    shiftLookupMaps={shiftLookupMaps}
                    nurses={displayNurses}
                    year={currentScheduleDate.year}
                    month={currentScheduleDate.month}
                    defaultShiftCode={shiftsForView}
                    showPerShiftStats={showPerShiftStats}
                    issuedSnapshotGroupId={issuedSnapshotGroupId}
                    currentGroupId={issuedSnapshotGroupId || null}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RosterViewAllTabContent;
